import type {DmApis, Extension, ExtensionAPI, InitializeExtArgs} from '@wix/document-manager-core'
import {ReportableError} from '@wix/document-manager-utils'
import type {
    BoundObjectOrInnerElement,
    BoundExtensionsPerProvider,
    CompRef,
    ExtensionBindingsMap,
    FunctionExtensionBindingsMap,
    FunctionPropertyBinding,
    ListContextBoundObjectsOptions,
    RuntimeBindingsConfig,
    RuntimeBindingsList,
    RuntimeBindingsObject,
    RuntimeContextBindingConfig,
    RuntimeFunctionBindingConfig,
    RuntimeFunctionBindingItem,
    RuntimeTemplateBindingConfig,
    TemplateExtensionBindingsMap
} from '@wix/document-services-types'
import _ from 'lodash'
import {DATA_TYPES} from '../../../constants/constants'
import {BindingType, RuntimeBindingsConfigType} from '../../../constants/dsTypesConstants'
import type {StructureExtensionAPI} from '../../structure'
import type {DataModelExtensionAPI} from '../../dataModel/dataModel'
import type {HooksExtensionApi} from '../../hooks/hooks'
import {CONTEXTS_HOOKS, type ContextDetachAfterEvent} from '../../contexts/hooks'
import type {InnerElementsExtensionAPI} from '../../builderComponents/innerElements'
import type {RelationshipsAPI} from '../../relationships'
import {getInnerElementPathAndOwner, walkSubtree, type TreeWalkVisitor} from '../../bindings/utils'
import {componentHasContextType, createBindingsValidators, hasBindingConfigMatching} from '../../bindings/bindingsUtils'
import {stripHashIfExists} from '../../../utils/refArrayUtils'

type RuntimeBindingsExtensionAPI = ExtensionAPI & {
    runtimeBindings: RuntimeBindingsObject
}

const RUNTIME_BINDINGS_NAMESPACE = DATA_TYPES.runtimeBindings

const createExtension = (): Extension => {
    const throwRuntimeBindingsError = (method: string, message: string): never => {
        throw new ReportableError({errorType: 'RuntimeBindingsError', message: `runtimeBindings.${method}: ${message}`})
    }

    const updateRuntimeBindingsList = (
        dataModel: DataModelExtensionAPI['dataModel'],
        pointer: CompRef,
        existingList: RuntimeBindingsList,
        updatedConfigs: RuntimeBindingsConfig[]
    ): void => {
        if (_.isEmpty(updatedConfigs)) {
            dataModel.components.removeItem(pointer, RUNTIME_BINDINGS_NAMESPACE)
            return
        }

        dataModel.components.addItem(pointer, RUNTIME_BINDINGS_NAMESPACE, {
            ...existingList,
            data: updatedConfigs
        })
    }

    const mergeBindingsMap = <T>(
        existingMap: Record<string, Record<string, T>>,
        newMap: Record<string, Record<string, T>>
    ) =>
        Object.entries(newMap).reduce<Record<string, Record<string, T>>>(
            (acc, [extensionName, modesMap]) => ({
                ...acc,
                [extensionName]: {
                    ...(acc[extensionName] ?? {}),
                    ...modesMap
                }
            }),
            _.mapValues(existingMap, modesMap => ({...modesMap}))
        )

    const removeModesFromBindingsMap = <T>(
        bindingsMap: Record<string, Record<string, T>>,
        modesToRemove: Record<string, string[]>
    ): Record<string, Record<string, T>> =>
        Object.entries(bindingsMap).reduce<Record<string, Record<string, T>>>((acc, [extensionName, modesMap]) => {
            const updatedModes = modesToRemove[extensionName]
                ? _.omit(modesMap, modesToRemove[extensionName])
                : modesMap
            if (!_.isEmpty(updatedModes)) {
                acc[extensionName] = updatedModes
            }
            return acc
        }, {})

    const removeModesFromRuntimeConfig = <T extends RuntimeBindingsConfig>(
        config: T,
        modesToRemove: Record<string, string[]>
    ): T => ({
        ...config,
        extensionsMap: removeModesFromBindingsMap(
            config.extensionsMap as Record<string, Record<string, unknown>>,
            modesToRemove
        ) as T['extensionsMap']
    })

    const toStoredFunctionBindingsMap = (
        bindingsMapPerMode: FunctionExtensionBindingsMap
    ): Record<string, Record<string, RuntimeFunctionBindingItem>> =>
        Object.entries(bindingsMapPerMode).reduce<Record<string, Record<string, RuntimeFunctionBindingItem>>>(
            (acc, [extensionName, modesMap]) => ({
                ...acc,
                [extensionName]: Object.entries(modesMap).reduce<Record<string, RuntimeFunctionBindingItem>>(
                    (modesAcc, [modeName, binding]) => ({
                        ...modesAcc,
                        [modeName]: {
                            function: binding.function,
                            asCallback: binding.callOptions.asCallback,
                            arguments: binding.callOptions.arguments
                        }
                    }),
                    {}
                )
            }),
            {}
        )

    const toPublicFunctionBindingsMap = (
        bindingsMapPerMode: Record<string, Record<string, RuntimeFunctionBindingItem>>
    ): FunctionExtensionBindingsMap =>
        Object.entries(bindingsMapPerMode).reduce<FunctionExtensionBindingsMap>(
            (acc, [extensionName, modesMap]) => ({
                ...acc,
                [extensionName]: Object.entries(modesMap).reduce<Record<string, FunctionPropertyBinding>>(
                    (modesAcc, [modeName, binding]) => ({
                        ...modesAcc,
                        [modeName]: {
                            function: binding.function,
                            callOptions: {
                                asCallback: binding.asCallback,
                                arguments: binding.arguments
                            }
                        }
                    }),
                    {}
                )
            }),
            {}
        )

    const createExtensionAPI = ({extensionAPI, pointers, dal}: DmApis): RuntimeBindingsExtensionAPI => {
        const {dataModel} = extensionAPI as DataModelExtensionAPI & StructureExtensionAPI
        const {innerElements} = extensionAPI as InnerElementsExtensionAPI
        const {relationships} = extensionAPI as RelationshipsAPI
        const {validateCompRef, validateComponentExists} = createBindingsValidators({
            errorType: 'RuntimeBindingsError',
            apiName: 'runtimeBindings',
            dataModel
        })

        const validateProviderType = (method: string, providerType: string, label: string): void => {
            if (!providerType) {
                throwRuntimeBindingsError(method, `${label} is required`)
            }
            if (!_.isString(providerType)) {
                throwRuntimeBindingsError(method, `${label} must be a string`)
            }
        }

        const getRuntimeBindingsList = (pointer: CompRef): RuntimeBindingsList | undefined =>
            dataModel.components.getItem(pointer, RUNTIME_BINDINGS_NAMESPACE) as RuntimeBindingsList | undefined

        const getRuntimeBindingsListOrThrow = (pointer: CompRef, method: string): RuntimeBindingsList => {
            const runtimeBindingsList = getRuntimeBindingsList(pointer)
            if (runtimeBindingsList === undefined) {
                throwRuntimeBindingsError(method, `This component does not have runtime bindings, id:${pointer.id}`)
            }

            return runtimeBindingsList as RuntimeBindingsList
        }

        const pushUniqueBoundObject = (
            results: BoundObjectOrInnerElement[],
            seen: Set<string>,
            result: BoundObjectOrInnerElement
        ): void => {
            const key =
                result.type === 'BoundObject'
                    ? `boundObject:${result.boundObjectRef.compRef.id}`
                    : `innerElement:${result.compRef.id}:${result.path.join('|')}`

            if (seen.has(key)) {
                return
            }

            seen.add(key)
            results.push(result)
        }

        const collectBoundObjectsFromComponent = (
            compRef: CompRef,
            predicate: (config: RuntimeBindingsConfig) => boolean,
            results: BoundObjectOrInnerElement[],
            seen: Set<string>
        ): void => {
            const runtimeBindingsList = getRuntimeBindingsList(compRef)
            if (hasBindingConfigMatching(runtimeBindingsList, predicate)) {
                pushUniqueBoundObject(results, seen, {type: 'BoundObject', boundObjectRef: {compRef}})
            }
        }

        const collectBoundObjectsFromInnerElement = (
            compRef: CompRef,
            path: string[],
            innerElementId: string,
            predicate: (config: RuntimeBindingsConfig) => boolean,
            results: BoundObjectOrInnerElement[],
            seen: Set<string>
        ): void => {
            const innerElementPointer = {id: innerElementId, type: DATA_TYPES.innerElements} as CompRef
            const runtimeBindingsList = getRuntimeBindingsList(innerElementPointer)

            if (hasBindingConfigMatching(runtimeBindingsList, predicate)) {
                pushUniqueBoundObject(results, seen, {type: 'InnerElement', compRef, path})
            }
        }

        const forEachMatchingOwner = (
            predicate: (config: RuntimeBindingsConfig) => boolean,
            onOwner: (ownerPointer: CompRef) => void
        ): void => {
            const seenOwnerPointers = new Set<string>()

            for (const pointer of dal.getAllItemsInNamespace(RUNTIME_BINDINGS_NAMESPACE)) {
                const item = dal.get(pointer)
                if (!item || item.type !== 'RuntimeBindingsList') {
                    continue
                }

                const refs: string[] = item.data ?? []
                const hasMatch = refs.some(ref => {
                    const config = dal.get({
                        id: stripHashIfExists(ref),
                        type: RUNTIME_BINDINGS_NAMESPACE
                    }) as RuntimeBindingsConfig | undefined

                    return Boolean(config && predicate(config))
                })

                if (!hasMatch) {
                    continue
                }

                const ownerPointers = relationships.getReferencesToPointer(pointer)
                if (!ownerPointers.length) {
                    continue
                }

                const ownerPointer = ownerPointers[0] as CompRef
                const ownerKey = `${ownerPointer.type}:${ownerPointer.id}`
                if (seenOwnerPointers.has(ownerKey)) {
                    continue
                }

                seenOwnerPointers.add(ownerKey)
                onOwner(ownerPointer)
            }
        }

        const resolveRuntimeBindingOwner = (ownerPointer: CompRef): BoundObjectOrInnerElement | undefined => {
            if (ownerPointer.type === DATA_TYPES.innerElements) {
                const {path, compRef} = getInnerElementPathAndOwner(
                    ownerPointer.id,
                    extensionAPI as RelationshipsAPI,
                    dal
                )
                if (!compRef) {
                    return undefined
                }

                return {type: 'InnerElement', compRef, path}
            }

            return {type: 'BoundObject', boundObjectRef: {compRef: ownerPointer}}
        }

        const listBoundObjectsForContext = (
            contextType: string,
            options?: ListContextBoundObjectsOptions
        ): BoundObjectOrInnerElement[] => {
            validateProviderType('contexts.listBoundObjects', contextType, 'contextType')

            const predicate = (config: RuntimeBindingsConfig) =>
                config.type === RuntimeBindingsConfigType.ContextBinding && config.contextProvider === contextType

            const results: BoundObjectOrInnerElement[] = []
            const seen = new Set<string>()

            if (options?.restrictUnderContainer) {
                validateCompRef('contexts.listBoundObjects', options.restrictUnderContainer)
                validateComponentExists('contexts.listBoundObjects', options.restrictUnderContainer)

                const visitor: TreeWalkVisitor = {
                    onComponent: compRef => collectBoundObjectsFromComponent(compRef, predicate, results, seen),
                    onInnerElement: (compRef, path, innerElementId) =>
                        collectBoundObjectsFromInnerElement(compRef, path, innerElementId, predicate, results, seen),
                    shouldStopAtComponent: compRef => componentHasContextType(compRef, contextType, dataModel)
                }

                walkSubtree(options.restrictUnderContainer, pointers, extensionAPI, visitor)
                return results
            }

            forEachMatchingOwner(predicate, ownerPointer => {
                const resolved = resolveRuntimeBindingOwner(ownerPointer)
                if (!resolved) {
                    return
                }

                pushUniqueBoundObject(results, seen, resolved)
            })

            return results
        }

        const listBoundObjectsForFunction = (functionLibraryType: string): BoundObjectOrInnerElement[] => {
            validateProviderType('functions.listBoundObjects', functionLibraryType, 'functionLibraryType')

            const predicate = (config: RuntimeBindingsConfig) =>
                config.type === RuntimeBindingsConfigType.FunctionBinding &&
                config.functionLibraryType === functionLibraryType

            const results: BoundObjectOrInnerElement[] = []
            const seen = new Set<string>()

            forEachMatchingOwner(predicate, ownerPointer => {
                const resolved = resolveRuntimeBindingOwner(ownerPointer)
                if (!resolved) {
                    return
                }

                pushUniqueBoundObject(results, seen, resolved)
            })

            return results
        }

        const replaceConfig = (
            configs: RuntimeBindingsConfig[],
            configToReplace: RuntimeBindingsConfig,
            updatedConfig: RuntimeBindingsConfig,
            matcher: (config: RuntimeBindingsConfig) => boolean
        ): RuntimeBindingsConfig[] =>
            configs.map(config => {
                if (config.id) {
                    return config.id === configToReplace.id ? updatedConfig : config
                }

                return matcher(config) ? updatedConfig : config
            })

        const upsertConfig = <T extends RuntimeBindingsConfig>(
            pointer: CompRef,
            newConfig: T,
            matcher: (config: RuntimeBindingsConfig) => config is T
        ): void => {
            const existingBindingsList = getRuntimeBindingsList(pointer)

            if (!existingBindingsList) {
                dataModel.components.addItem(pointer, RUNTIME_BINDINGS_NAMESPACE, {
                    type: 'RuntimeBindingsList',
                    data: [newConfig]
                })
                return
            }

            const configs = existingBindingsList.data ?? []
            const existingConfig = configs.find(matcher)

            if (!existingConfig) {
                dataModel.components.addItem(pointer, RUNTIME_BINDINGS_NAMESPACE, {
                    ...existingBindingsList,
                    data: [...configs, newConfig]
                })
                return
            }

            const updatedConfig: T = {
                ...existingConfig,
                extensionsMap: mergeBindingsMap(
                    existingConfig.extensionsMap as Record<string, Record<string, unknown>>,
                    newConfig.extensionsMap as Record<string, Record<string, unknown>>
                ) as T['extensionsMap']
            }
            updateRuntimeBindingsList(
                dataModel,
                pointer,
                existingBindingsList,
                replaceConfig(configs, existingConfig, updatedConfig, matcher)
            )
        }

        const bindContext = (pointer: CompRef, contextType: string, bindingsMapPerMode: ExtensionBindingsMap): void => {
            upsertConfig(
                pointer,
                {
                    type: RuntimeBindingsConfigType.ContextBinding,
                    contextProvider: contextType,
                    extensionsMap: bindingsMapPerMode
                },
                (config): config is RuntimeContextBindingConfig =>
                    config.type === RuntimeBindingsConfigType.ContextBinding && config.contextProvider === contextType
            )
        }

        const bindFunction = (
            pointer: CompRef,
            functionLibraryType: string,
            bindingsMapPerMode: FunctionExtensionBindingsMap
        ): void => {
            upsertConfig(
                pointer,
                {
                    type: RuntimeBindingsConfigType.FunctionBinding,
                    functionLibraryType,
                    extensionsMap: toStoredFunctionBindingsMap(bindingsMapPerMode)
                },
                (config): config is RuntimeFunctionBindingConfig =>
                    config.type === RuntimeBindingsConfigType.FunctionBinding &&
                    config.functionLibraryType === functionLibraryType
            )
        }

        const bindTemplate = (
            pointer: CompRef,
            templateComponentId: string,
            bindingsMapPerMode: TemplateExtensionBindingsMap
        ): void => {
            upsertConfig(
                pointer,
                {
                    type: RuntimeBindingsConfigType.TemplateBinding,
                    templateComponentId,
                    extensionsMap: bindingsMapPerMode
                },
                (config): config is RuntimeTemplateBindingConfig =>
                    config.type === RuntimeBindingsConfigType.TemplateBinding &&
                    config.templateComponentId === templateComponentId
            )
        }

        const unbindExtensionModesFromPointer = (
            pointer: CompRef,
            modes: Record<string, string[]>,
            method: string
        ): void => {
            const runtimeBindingsList = getRuntimeBindingsListOrThrow(pointer, method)
            const configs = runtimeBindingsList.data ?? []
            const updatedConfigs = configs
                .map(config => removeModesFromRuntimeConfig(config, modes))
                .filter(config => !_.isEmpty(config.extensionsMap))

            updateRuntimeBindingsList(dataModel, pointer, runtimeBindingsList, updatedConfigs)
        }

        const getBoundExtensionsFromPointer = (pointer: CompRef): BoundExtensionsPerProvider => {
            const existingBindingsList = getRuntimeBindingsList(pointer)

            if (!existingBindingsList?.data?.length) {
                return {}
            }

            return existingBindingsList.data.reduce<BoundExtensionsPerProvider>((acc, config) => {
                if (config.type === RuntimeBindingsConfigType.ContextBinding) {
                    acc[config.contextProvider] = {
                        bindingType: BindingType.ContextProvider,
                        contextType: config.contextProvider,
                        extensionsMap: config.extensionsMap
                    }
                    return acc
                }

                if (config.type === RuntimeBindingsConfigType.FunctionBinding) {
                    acc[config.functionLibraryType] = {
                        bindingType: BindingType.FunctionLibrary,
                        functionLibraryType: config.functionLibraryType,
                        extensionsMap: toPublicFunctionBindingsMap(config.extensionsMap)
                    }
                    return acc
                }

                if (config.type === RuntimeBindingsConfigType.TemplateBinding) {
                    acc[config.templateComponentId] = {
                        bindingType: BindingType.Template,
                        templateComponentId: config.templateComponentId,
                        extensionsMap: config.extensionsMap
                    }
                    return acc
                }

                return acc
            }, {})
        }

        const unbindPointer = (pointer: CompRef): void => {
            const existingBindingsList = getRuntimeBindingsList(pointer)
            if (!existingBindingsList) {
                return
            }

            dataModel.components.removeItem(pointer, RUNTIME_BINDINGS_NAMESPACE)
        }

        const getExistingInnerElementPointer = (compRef: CompRef, innerElementPath: string[]): CompRef | undefined => {
            const innerElement = innerElements.getInnerElement(compRef, innerElementPath)
            if (!innerElement) {
                return undefined
            }

            return pointers.getPointer(innerElement.id!, DATA_TYPES.innerElements) as CompRef
        }

        const getExistingInnerElementPointerOrThrow = (
            compRef: CompRef,
            innerElementPath: string[],
            method: string
        ): CompRef => {
            const innerElementPointer = getExistingInnerElementPointer(compRef, innerElementPath)
            if (innerElementPointer === undefined) {
                throwRuntimeBindingsError(method, `Inner element not found: ${innerElementPath.join('|')}`)
            }

            return innerElementPointer as CompRef
        }

        return {
            runtimeBindings: {
                bind: (compRef: CompRef, contextType: string, bindingsMapPerMode: ExtensionBindingsMap): void => {
                    validateCompRef('bind', compRef)
                    validateProviderType('bind', contextType, 'contextType')
                    validateComponentExists('bind', compRef)
                    bindContext(compRef, contextType, bindingsMapPerMode)
                },
                getBoundExtensions: (compRef: CompRef): BoundExtensionsPerProvider => {
                    validateCompRef('getBoundExtensions', compRef)
                    validateComponentExists('getBoundExtensions', compRef)
                    return getBoundExtensionsFromPointer(compRef)
                },
                unbindExtensionModes: (compRef: CompRef, modes: Record<string, string[]>): void => {
                    validateCompRef('unbindExtensionModes', compRef)
                    validateComponentExists('unbindExtensionModes', compRef)
                    unbindExtensionModesFromPointer(compRef, modes, 'unbindExtensionModes')
                },
                unbind: (compRef: CompRef): void => {
                    validateCompRef('unbind', compRef)
                    validateComponentExists('unbind', compRef)
                    unbindPointer(compRef)
                },
                contexts: {
                    listBoundObjects: (
                        contextType: string,
                        options?: ListContextBoundObjectsOptions
                    ): BoundObjectOrInnerElement[] => listBoundObjectsForContext(contextType, options)
                },
                functions: {
                    bind: (
                        compRef: CompRef,
                        functionLibraryType: string,
                        bindingsMapPerMode: FunctionExtensionBindingsMap
                    ): void => {
                        validateCompRef('functions.bind', compRef)
                        validateProviderType('functions.bind', functionLibraryType, 'functionLibraryType')
                        validateComponentExists('functions.bind', compRef)
                        bindFunction(compRef, functionLibraryType, bindingsMapPerMode)
                    },
                    listBoundObjects: (functionLibraryType: string): BoundObjectOrInnerElement[] =>
                        listBoundObjectsForFunction(functionLibraryType)
                },
                templates: {
                    bind: (
                        compRef: CompRef,
                        templateComponentId: string,
                        bindingsMapPerMode: TemplateExtensionBindingsMap
                    ): void => {
                        validateCompRef('templates.bind', compRef)
                        validateProviderType('templates.bind', templateComponentId, 'templateComponentId')
                        validateComponentExists('templates.bind', compRef)
                        bindTemplate(compRef, templateComponentId, bindingsMapPerMode)
                    }
                },
                innerElements: {
                    bind: (
                        compRef: CompRef,
                        innerElementPath: string[],
                        contextType: string,
                        bindingsMapPerMode: ExtensionBindingsMap
                    ): void => {
                        validateCompRef('innerElements.bind', compRef)
                        validateProviderType('innerElements.bind', contextType, 'contextType')
                        validateComponentExists('innerElements.bind', compRef)
                        const innerElementPointer = getExistingInnerElementPointerOrThrow(
                            compRef,
                            innerElementPath,
                            'innerElements.bind'
                        )
                        bindContext(innerElementPointer, contextType, bindingsMapPerMode)
                    },
                    getBoundExtensions: (compRef: CompRef, innerElementPath: string[]): BoundExtensionsPerProvider => {
                        validateCompRef('innerElements.getBoundExtensions', compRef)
                        validateComponentExists('innerElements.getBoundExtensions', compRef)
                        const innerElementPointer = getExistingInnerElementPointer(compRef, innerElementPath)
                        return innerElementPointer ? getBoundExtensionsFromPointer(innerElementPointer) : {}
                    },
                    unbindExtensionModes: (
                        compRef: CompRef,
                        innerElementPath: string[],
                        modes: Record<string, string[]>
                    ): void => {
                        validateCompRef('innerElements.unbindExtensionModes', compRef)
                        validateComponentExists('innerElements.unbindExtensionModes', compRef)
                        const innerElementPointer = getExistingInnerElementPointerOrThrow(
                            compRef,
                            innerElementPath,
                            'innerElements.unbindExtensionModes'
                        )
                        unbindExtensionModesFromPointer(
                            innerElementPointer,
                            modes,
                            'innerElements.unbindExtensionModes'
                        )
                    },
                    unbind: (compRef: CompRef, innerElementPath: string[]): void => {
                        validateCompRef('innerElements.unbind', compRef)
                        validateComponentExists('innerElements.unbind', compRef)
                        const innerElementPointer = getExistingInnerElementPointerOrThrow(
                            compRef,
                            innerElementPath,
                            'innerElements.unbind'
                        )
                        unbindPointer(innerElementPointer)
                    },
                    functions: {
                        bind: (
                            compRef: CompRef,
                            innerElementPath: string[],
                            functionLibraryType: string,
                            bindingsMapPerMode: FunctionExtensionBindingsMap
                        ): void => {
                            validateCompRef('innerElements.functions.bind', compRef)
                            validateProviderType(
                                'innerElements.functions.bind',
                                functionLibraryType,
                                'functionLibraryType'
                            )
                            validateComponentExists('innerElements.functions.bind', compRef)
                            const innerElementPointer = getExistingInnerElementPointerOrThrow(
                                compRef,
                                innerElementPath,
                                'innerElements.functions.bind'
                            )
                            bindFunction(innerElementPointer, functionLibraryType, bindingsMapPerMode)
                        }
                    },
                    templates: {
                        bind: (
                            compRef: CompRef,
                            innerElementPath: string[],
                            templateComponentId: string,
                            bindingsMapPerMode: TemplateExtensionBindingsMap
                        ): void => {
                            validateCompRef('innerElements.templates.bind', compRef)
                            validateProviderType(
                                'innerElements.templates.bind',
                                templateComponentId,
                                'templateComponentId'
                            )
                            validateComponentExists('innerElements.templates.bind', compRef)
                            const innerElementPointer = getExistingInnerElementPointerOrThrow(
                                compRef,
                                innerElementPath,
                                'templates.bind'
                            )
                            bindTemplate(innerElementPointer, templateComponentId, bindingsMapPerMode)
                        }
                    }
                }
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis): RuntimeBindingsExtensionAPI => {
        const {runtimeBindings} = extensionAPI as RuntimeBindingsExtensionAPI

        return {
            runtimeBindings: {
                bind: runtimeBindings.bind,
                getBoundExtensions: runtimeBindings.getBoundExtensions,
                unbindExtensionModes: runtimeBindings.unbindExtensionModes,
                unbind: runtimeBindings.unbind,
                contexts: {
                    listBoundObjects: runtimeBindings.contexts.listBoundObjects
                },
                functions: {
                    bind: runtimeBindings.functions.bind,
                    listBoundObjects: runtimeBindings.functions.listBoundObjects
                },
                templates: {
                    bind: runtimeBindings.templates.bind
                },
                innerElements: {
                    bind: runtimeBindings.innerElements.bind,
                    getBoundExtensions: runtimeBindings.innerElements.getBoundExtensions,
                    unbindExtensionModes: runtimeBindings.innerElements.unbindExtensionModes,
                    unbind: runtimeBindings.innerElements.unbind,
                    functions: {
                        bind: runtimeBindings.innerElements.functions.bind
                    },
                    templates: {
                        bind: runtimeBindings.innerElements.templates.bind
                    }
                }
            }
        }
    }

    const initialize = async ({extensionAPI, pointers}: InitializeExtArgs) => {
        const {hooks, dataModel} = extensionAPI as HooksExtensionApi & DataModelExtensionAPI

        const removeContextRuntimeBindings = (pointer: CompRef, contextType: string): void => {
            const runtimeBindingsList = dataModel.components.getItem(pointer, RUNTIME_BINDINGS_NAMESPACE)
            if (!runtimeBindingsList?.data?.length) {
                return
            }

            const filteredConfigs = runtimeBindingsList.data.filter(
                (config: RuntimeBindingsConfig) =>
                    config.type !== RuntimeBindingsConfigType.ContextBinding || config.contextProvider !== contextType
            )

            updateRuntimeBindingsList(dataModel, pointer, runtimeBindingsList, filteredConfigs)
        }

        const cleanupRuntimeBindingsAfterDetachedContext = (compRef: CompRef, contextType: string): void => {
            const visitor: TreeWalkVisitor = {
                onComponent: componentPointer => removeContextRuntimeBindings(componentPointer, contextType),
                onInnerElement: (_compRef, _path, innerElementId) =>
                    removeContextRuntimeBindings(
                        pointers.getPointer(innerElementId, DATA_TYPES.innerElements) as CompRef,
                        contextType
                    )
            }

            walkSubtree(compRef, pointers, extensionAPI, visitor)
        }

        hooks.registerHook<'context_detach_after', ContextDetachAfterEvent>(
            CONTEXTS_HOOKS.DETACH_AFTER.id,
            ({compRef, contextType}: ContextDetachAfterEvent) => {
                cleanupRuntimeBindingsAfterDetachedContext(compRef, contextType)
            }
        )
    }

    return {
        name: 'runtimeBindings',
        dependencies: new Set(['contexts', 'dataModel', 'structure', 'hooks', 'innerElements', 'relationships']),
        createExtensionAPI,
        createPublicAPI,
        initialize
    }
}

export {createExtension}
